<template>
  <div
    id="navbar-main"
    class="nav navbar navbar-expand-lg visible-lg-up"
    :class="{ 'fixed-bottom': fixedToBottom }"
    data-cy="menu-navbar"
    data-prevent-focus-scroll
  >
    <b-container fluid>
      <b-container class="container-slim aux-min-height navbar-main-container"
                   tag="nav"
                   :aria-label="$t('global.main_menu')"
      >
        <ul class="nav nav-fill text-center w-100 flex-nowrap justify-content-center" :style="minMaxStyleMenuItem">
          <slot v-for="menu in menus" :key="menu.key">
            <li v-if="menu.key !== 'notch'"
                key="menu-notch"
                class="nav-item d-flex justify-content-center"
            >
              <b-button
                variant="link"
                :data-cy="`menu-nav-item-${menu.key}`"
                class="nav-link px-0 btn"
                :class="{ active: menu.key === activeModule || isCalendar(menu) }"
                :aria-current="menu.key === activeModule ? 'page' : null"
                @click.prevent="navigate(menu.navigate)"
                @mouseover="hover = menu.key"
                @mouseleave="hover = null"
                @focusin="hover = menu.key"
                @focusout="hover = null"
              >
                <span class="position-relative nav-link-icon">
                  <span
                    v-if="menu.key === 'channels' && menu.key !== activeModule && unreadMessages > 0"
                    :data-cy="`unread-badge-${unreadMessages}`"
                    class="chatBadge border border-white font-weight-normal f-12 position-absolute d-flex justify-content-center br-12 align-items-center bg-primary white nano px-1"
                  >{{ unreadMessages > 0 ? ' ' : ' ' }}</span>
                  <!-- will be updated on next iteration -->
                  <sdui-icon
                    :class="menu.key === activeModule || menu.key === hover || isCalendar(menu) ? [menu.icon] : [menu.icon + '-o']"
                  />
                </span>
                <span class="w-100 font-weight-bold text-break">{{ menu.title }}</span>
              </b-button>
            </li>
            <li v-else
                :key="menu.key"
                :data-cy="`menu-nav-item-${menu.key}`"
                class="nav-item notch"
            >
              <b-button
                id="btn-notch"
                v-haptic-feedback
                class="btn bg-primary white text-center btn-notch"
                :class="{ active: showMenu }"
                data-cy="news-notch"
                variant="link"
                @click="showMenu = !showMenu"
              >
                <span class="sr-only">{{ $t('global.notch.title') }}</span>
                <sdui-icon class="icon-close-o" />
              </b-button>

              <div class="notch-space-behind"/>
            </li>
          </slot>
        </ul>
      </b-container>
    </b-container>
    <main-menu id="sdui"
               v-model="showMenu"
               :prevent-focus-return-on-modal-close="preventFocusReturnOnModalClose"
    >
      <template v-if="showMenu">
        <main-menu-option
          v-for="action in defaultActions"
          :key="action.title"
          :title="action.title"
          :action="action"
          :disabled="action.disabledWithLoadingSpinner"
          :data-cy="action.dataCy || action.routeName"
          :data-test="`menu-action-${action.title}`"
          @[customEventHandlerName(action)]="customEventHandlerFn(action)"
        >
          <template v-if="action.disabledWithLoadingSpinner">
            <div class="d-flex justify-content-between align-items-center w-100">
              {{ action.title }}
              <sdui-loader class="nav-bar-loading-spinner mr-3" />
            </div>
          </template>
          <template v-else>{{ action.title }}</template>
        </main-menu-option>
      </template>
      <b-button
        v-haptic-feedback
        class="btn bg-primary white text-center btn-notch btn-notch--close active"
        data-cy="news-notch-close"
        variant="link"
        @click="showMenu = !showMenu"
      >
        <span class="sr-only">{{ $t('global.close') }}</span>
        <sdui-icon class="icon-close-o" />
      </b-button>
    </main-menu>
  </div>
</template>
<script>
import MainMenuOption from 'src/components/miscellaneous/ui/notch/MainMenuOption.vue'
import MainMenu from 'src/components/miscellaneous/ui/notch/MainMenu.vue'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'NavBar',
  components: { MainMenu, MainMenuOption, SduiLoader },
  props: {
    menus: { type: Array, default: () => [] },
    modelValue: { type: Boolean, default: false },
    defaultActions: { type: Array, default: () => [] },
    fixedToBottom: { type: Boolean, default: true },
  },

  data: () => ({
    hover: null,
    showMenu: false,
    preventFocusReturnOnModalClose: false,
  }),

  computed: {
    activeModule() {
      return this.$route?.meta?.group
    },

    unreadMessages() {
      return this.$store.state.channel.unreadMessages
    },

    minMaxStyleMenuItem() {
      // This rather hacky solution is needed to make sure that the notch button (if its visible)
      // is centered at all times, in all languages. This in turn is needed because the MainMenu is a modal which is
      // centered by default. It has a close button as well which is visually overlapped by the notch button.
      // If those two buttons are not at the same coordinates and keyboard focus is activated to close the notch menu,
      // the user would possibly see both buttons.
      // TODO long-term: Refactor the whole NavBar to not use Modal logic
      const displaysNotch = this.menus.some((menu) => menu.key === 'notch')
      if (!displaysNotch) return `--maxmin-width: calc(100% / ${this.menus.length})`
      return `--maxmin-width: calc((100% - var(--notch-min-max-width)) / ${this.menus.length - 1})`
    },
  },

  emits: ['update:modelValue'],

  methods: {
    isCalendar({ key }) {
      return key === 'timetable' && this.activeModule === 'calendar'
    },

    navigate({ name, params = {}, query = {} }) {
      if (this.$route.name === name) return

      // For some reason, this.$router.push({ name, replace }) is actually pushing a new history entry instead of replacing it.
      if (this.$route.name === 'login-sign-in' || this.$route.name === 'authenticator-start') {
        return this.$router.replace({ name })
      }
      this.$router.push({ name: name, params: params, query: query })
    },

    customEventHandlerName(action) {
      return action.customEvent?.name || null
    },

    customEventHandlerFn(action) {
      this.preventFocusReturnOnModalClose = action.preventFocusReturnOnModalClose || false
      return action.customEvent?.fn() || null
    },
  },

  watch: {
    modelValue(newVal) {
      this.showMenu = newVal
    },

    showMenu(newVal) {
      this.$emit('update:modelValue', newVal)
      if (newVal) clyTracking.trackApp().interaction.mainMenuClick()
    },
  },
}
</script>

<style scoped lang="scss">
.aux-min-height {
  min-height: 48px;
}

.notch {
  background-size: 120%;
  background-color: transparent;
  position: relative;
  background-image: url('/img/miscellaneous/ui/notch.svg');
  min-width: var(--notch-min-max-width);
  max-width: var(--notch-min-max-width);
  background-position: center -7px;
  background-repeat: no-repeat;
  margin-top: 0;

  &::after,
  &::before {
    content: '';
    position: absolute;
    height: 100%;
    top: 0;
    width: 50vw;
    background-color: $color-bg-primary;
    box-shadow: 0 -1px 4px 0 rgba($color-black-rgb, 0.08);
    z-index: -1;
  }

  &::before {
    left: 100%;
  }

  &::after {
    right: 100%;
  }
}
#navbar-main {
  --notch-min-max-width: 133px;

  padding: 0;
  padding-bottom: env(save-area-inset-bottom) !important;
  background: transparent !important;
  &.authenticator {
    box-shadow: 0 -1px 4px 0 rgba($color-black-rgb, 0.08) !important;
    background: $color-white !important;
    ul {
      margin-bottom: 4px !important;
      margin-top: 4px !important;
    }
  }

  .nav-item:not(.notch) {
    max-width: var(--maxmin-width);
    min-width: var(--maxmin-width);
  }

  .nav-link {
    width: 100%;
    background: $color-white;
    color: $color-gray-300;
    text-align: center;
    font-size: pxtorem(9);
    /* stylelint-disable-next-line declaration-property-value-allowed-list */
    line-height: min(1.25rem, 28px);
    padding: 3.2px 12.8px;
    user-select: none;
    align-self: baseline;

    span:not(.icon) {
      /* stylelint-disable-next-line declaration-property-value-allowed-list */
      font-size: min(0.8125rem, 26px);
      width: 50px;
      display: block;
      text-align: center;
    }

    &:hover,
    &:focus-visible,
    &.active {
      color: $color-primary;
    }

    &:focus-visible {
      text-decoration: underline;
      text-decoration-thickness: 2px;
      z-index: 1;
    }

    .icon {
      text-decoration: none;
      /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
      font-size: 28px;
      font-weight: bolder;
    }
  }

  .nav-link-icon {
    margin: 0 auto;
  }

  .router-link:hover,
  .router-link-active {
    // todo: check why $primary is in sdui colors
    color: $color-primary;
  }

  .router-link-active i {
    text-transform: uppercase;
  }

  &::after {
    top: 90%;
  }
  &.without-notch {
    box-shadow: 0 -1px 4px 0 rgba($color-black-rgb, 0.08);
    background: $color-white !important;
  }
}

.notch-space-behind {
  position: absolute;
  width: 101%;
  bottom: 0;
  background-color: white;
  left: -1px;
  height: calc(100% - 50px);
}

@media only screen and (max-width: 768px) {
  #navbar-main .nav-link.active i {
    text-transform: none !important;
  }
}
.chatBadge {
  top: -3px;
  right: -1px;
  max-width: 25px;
  min-height: 18px;
}

.btn-notch--close {
  opacity: 0;
  position: absolute;
  box-shadow: none !important;
  bottom: -72px !important; // ensure close button is on the same position as the open button

  &:focus-visible {
    opacity: 1;
  }
}

.nav-bar-loading-spinner {
  height: 24px;
  width: 24px;
}
</style>
